import { Alert } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  RebuildRouteDropdownAvoidTollsRow,
  RebuildRouteDropdownSkipCacheRow,
  RebuildRouteProviderSelector,
} from "@ryde/controls";
import { BaseDrawer, FormDrawer } from "@ryde/layout";
import {
  useHubRideActionConfirm,
  usePageContext,
  useRidesBulkActions,
} from "@ryde/redux";
import {
  bulkRebuildRoutesCustomDrawerAtom,
  DirectionsServiceEnum,
  HubRideActionKind,
  PROVIDERS_WITH_AVOID_TOLLS,
  RebuildRouteTypeEnum,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
  useSystemParams,
} from "@ryde/shared";

import {
  buildBulkRebuildRoutesCustomPayload,
  BulkRebuildRoutesCustomContentStyled,
  BulkRebuildRoutesCustomOptionsSelector,
  RIDE_BULK_REBUILD_ROUTES_CUSTOM,
} from "./parts";

export const BulkRebuildRoutesCustomRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const systemParams = useSystemParams();
  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);
  const setDrawerOpened = useSetRecoilState(bulkRebuildRoutesCustomDrawerAtom);
  const { bulkRebuildRoutes } = useRidesBulkActions();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();

  const systemDefaultProvider =
    systemParams.directionsService || DirectionsServiceEnum.Google;
  const systemDefaultAvoidTolls = systemParams.avoidTolls ?? false;

  const [selectedProvider, setSelectedProvider] =
    useState<DirectionsServiceEnum>(systemDefaultProvider);
  const [routeOption, setRouteOption] = useState<RebuildRouteTypeEnum>(
    RIDE_BULK_REBUILD_ROUTES_CUSTOM.defaultRebuildType,
  );
  const [avoidTolls, setAvoidTolls] = useState(systemDefaultAvoidTolls);
  const [skipCachedPaths, setSkipCachedPaths] = useState(true);
  const [skipMlPath, setSkipMlPath] = useState(false);

  const supportsAvoidTolls =
    PROVIDERS_WITH_AVOID_TOLLS.includes(selectedProvider);

  const totalRidesCount = selectedRideIds.length;

  const onCloseDrawer = useEvent((): void => {
    setDrawerOpened(false);
  });

  const onSaveClick = useEvent(async (): Promise<void> => {
    const isHubModifyConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubModifyConfirmed) {
      return;
    }

    const { ids, ...rebuildOptions } = buildBulkRebuildRoutesCustomPayload({
      ids: selectedRideIds,
      rebuildType: routeOption,
      provider: selectedProvider,
      avoidTolls,
      skipCachedPaths,
      skipMlPath,
    });
    const result = await bulkRebuildRoutes(ids, rebuildOptions);

    if (result?.success) {
      onCloseDrawer();
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <FormDrawer
        onCloseDrawer={onCloseDrawer}
        onCancelClick={onCloseDrawer}
        title={t("general.REBUILD_ROUTES_CUSTOM")}
        onSaveClick={onSaveClick}
        footerSaveLabel={t("general.REBUILD_ROUTES")}
      >
        <BulkRebuildRoutesCustomContentStyled>
          <Alert severity="info" icon={false}>
            {t("general.REBUILD_ROUTES_CUSTOM_SELECTED", {
              count: totalRidesCount,
            })}
          </Alert>
          <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--providers">
            <RebuildRouteProviderSelector
              selectedProvider={selectedProvider}
              systemDefaultProvider={systemDefaultProvider}
              showLastUsedBadge={false}
              onProviderChange={setSelectedProvider}
            />
          </div>
          <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--options">
            <BulkRebuildRoutesCustomOptionsSelector
              routeOption={routeOption}
              selectedProvider={selectedProvider}
              onRouteOptionChange={setRouteOption}
            />
          </div>
          <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--checks">
            {supportsAvoidTolls && (
              <RebuildRouteDropdownAvoidTollsRow
                checked={avoidTolls}
                isDefaultAvoidTolls={systemDefaultAvoidTolls}
                onChange={setAvoidTolls}
              />
            )}
            <RebuildRouteDropdownSkipCacheRow
              checked={skipCachedPaths}
              label={t("general.SKIP_CACHE")}
              tooltip={t("general.REBUILD_ROUTES_CUSTOM_SKIP_CACHE_TOOLTIP")}
              onChange={setSkipCachedPaths}
            />
            <RebuildRouteDropdownSkipCacheRow
              checked={skipMlPath}
              label={t("general.SKIP_SAVED_ML_PATH")}
              tooltip={t("general.SKIP_SAVED_ML_PATH_TOOLTIP")}
              onChange={setSkipMlPath}
            />
          </div>
        </BulkRebuildRoutesCustomContentStyled>
      </FormDrawer>
    </BaseDrawer>
  );
};
